import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { LanguageProvider } from "@/i18n/LanguageContext";

const TermsContent = () => (
  <div className="min-h-screen bg-background">
    <Navbar />
    <div className="container mx-auto px-4 py-24 max-w-3xl">
      <h1 className="font-display text-3xl md:text-4xl text-foreground mb-8">Terms of Service</h1>
      <p className="text-muted-foreground mb-8 text-sm">Last updated: March 2026</p>

      <div className="prose prose-lg max-w-none text-foreground/80">
        <p>
          Welcome to Vita Dom. By accessing or using our website and services, you agree to be bound
          by these Terms of Service. Please read them carefully.
        </p>

        <h2 className="font-display text-xl text-foreground mt-10 mb-4">1. Service Description</h2>
        <p>
          Vita Dom provides home care coordination services connecting families with local caregivers
          in Serbia, Romania, North Macedonia, Bulgaria, and Hungary. Our services include but are
          not limited to:
        </p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Errands and grocery shopping assistance</li>
          <li>Cooking and meal preparation</li>
          <li>Cleaning and household maintenance</li>
          <li>Walks, companionship, and social support</li>
          <li>Pharmacy visits and health-related assistance</li>
        </ul>
        <p className="mt-4">
          Vita Dom acts as a coordination platform. Caregivers are vetted and managed by us, but the
          specific services delivered may vary based on local availability and individual needs.
        </p>

        <h2 className="font-display text-xl text-foreground mt-10 mb-4">2. Booking & Payment</h2>
        <p>
          To book a care plan, you must provide accurate personal information and select a service
          plan. Key terms:
        </p>
        <ul className="list-disc pl-6 space-y-2">
          <li>A <strong>€10 reservation fee</strong> is required to secure your booking. This fee is deducted from your first monthly payment.</li>
          <li>All payments are processed securely through <strong>Stripe</strong>. We do not store your credit card information.</li>
          <li>Monthly subscription fees are charged at the beginning of each billing cycle.</li>
          <li>Prices are displayed in Euros (€) and include all applicable taxes unless otherwise stated.</li>
          <li>You may also express interest without payment; a team member will follow up to discuss options.</li>
        </ul>

        <h2 className="font-display text-xl text-foreground mt-10 mb-4">3. Cancellation & Refunds</h2>
        <ul className="list-disc pl-6 space-y-2">
          <li>You may <strong>cancel your subscription at any time</strong> without penalties by contacting us.</li>
          <li>Cancellation takes effect at the end of the current billing period. No partial refunds are issued for the remaining days of a billing cycle.</li>
          <li>The <strong>€10 reservation fee is non-refundable</strong> once services have been initiated. If we are unable to provide service in your area, the fee will be fully refunded.</li>
          <li>Refund requests for exceptional circumstances will be reviewed on a case-by-case basis.</li>
        </ul>

        <h2 className="font-display text-xl text-foreground mt-10 mb-4">4. User Responsibilities</h2>
        <p>By using our services, you agree to:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Provide accurate and complete information during registration</li>
          <li>Treat caregivers with respect and dignity</li>
          <li>Notify us promptly of any changes to care requirements or contact information</li>
          <li>Not use our services for any unlawful purpose</li>
        </ul>

        <h2 className="font-display text-xl text-foreground mt-10 mb-4">5. Limitation of Liability</h2>
        <p>
          Vita Dom strives to provide the highest quality care coordination. However:
        </p>
        <ul className="list-disc pl-6 space-y-2">
          <li>We are not a medical or emergency service provider. In case of medical emergencies, please contact local emergency services immediately.</li>
          <li>We are not liable for any indirect, incidental, or consequential damages arising from the use of our services.</li>
          <li>Our total liability is limited to the amount paid by you for services in the preceding 3 months.</li>
          <li>We make reasonable efforts to vet caregivers but cannot guarantee against all risks.</li>
        </ul>

        <h2 className="font-display text-xl text-foreground mt-10 mb-4">6. Intellectual Property</h2>
        <p>
          All content on this website, including text, graphics, logos, and software, is the property
          of Vita Dom or its licensors and is protected by applicable intellectual property laws. You
          may not reproduce, distribute, or create derivative works without our written permission.
        </p>

        <h2 className="font-display text-xl text-foreground mt-10 mb-4">7. Modifications</h2>
        <p>
          We reserve the right to modify these Terms of Service at any time. Changes will be posted
          on this page with an updated "Last updated" date. Continued use of our services after
          changes constitutes acceptance of the revised terms.
        </p>

        <h2 className="font-display text-xl text-foreground mt-10 mb-4">8. Governing Law</h2>
        <p>
          These Terms of Service are governed by and construed in accordance with the laws of the
          European Union and the applicable national laws of the country where the service is
          provided. Any disputes shall be resolved through the competent courts of the relevant
          jurisdiction.
        </p>

        <h2 className="font-display text-xl text-foreground mt-10 mb-4">9. Contact</h2>
        <p>
          If you have questions about these Terms of Service, please contact us:
        </p>
        <ul className="list-none pl-0 space-y-1 mt-4">
          <li><strong>Vita Dom</strong></li>
          <li>Email: <a href="mailto:office@vita-dom.org" className="text-primary hover:underline">office@vita-dom.org</a></li>
        </ul>
      </div>
    </div>
    <Footer />
  </div>
);

export default function Terms() {
  return (
    <LanguageProvider>
      <TermsContent />
    </LanguageProvider>
  );
}
